/**
 * PhotoCapture — camera capture + presigned-PUT upload for a store report.
 *
 * Flow (from the backend contract):
 *   1. POST /reports/{id}/photos { content_type } -> { photo_id, key, upload_url }
 *   2. PUT the raw image bytes to upload_url (a 10-min S3 presigned PUT).
 *
 * The picker is the shared FileUpload with `capture="environment"`, so a tap on
 * a tablet still opens the rear camera and a desktop still gets the file
 * dialog. It takes SEVERAL photos in one go now: each is uploaded in turn and
 * appended to the list, so a closing walk-through is one trip instead of one
 * tap per shot.
 *
 * Already-uploaded photos for this report render as thumbnails via a presigned
 * GET (StorePhotoThumb).
 */

import { useState } from 'react';
import { Camera } from 'lucide-react';
import { FileUpload, type UploadFile } from '@ampersandbrands/ui';
import {
  useRequestPhotoUpload,
  useStorePhotoUrl,
  type ReportPhoto,
} from '@/lib/useStores';

export function PhotoCapture({
  reportId,
  existing,
}: {
  reportId: number;
  existing: ReportPhoto[];
}) {
  const request = useRequestPhotoUpload();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track photo ids uploaded in this session so they render immediately without
  // a full report refetch. The persisted list arrives on the next reports load.
  const [justUploaded, setJustUploaded] = useState<number[]>([]);
  const [staged, setStaged] = useState<UploadFile[]>([]);

  // Upload one photo and append it to the list. Each shot is uploaded on its
  // own so one bad file does not lose the rest of the batch.
  const uploadOne = async (file: File) => {
    const { photo_id, upload_url } = await request.mutateAsync({
      reportId,
      body: { content_type: file.type || 'image/jpeg' },
    });
    const putRes = await fetch(upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'image/jpeg' },
      body: file,
    });
    if (!putRes.ok) {
      throw new Error(`Upload failed (${putRes.status})`);
    }
    setJustUploaded((prev) => [...prev, photo_id]);
  };

  const handleFiles = async (files: File[]) => {
    setError(null);
    setUploading(true);
    const failures: string[] = [];
    for (const file of files) {
      try {
        await uploadOne(file);
      } catch (e) {
        failures.push(`${file.name || 'photo'}: ${e instanceof Error ? e.message : 'Upload failed'}`);
      }
    }
    setUploading(false);
    setError(failures.length > 0 ? failures.join('; ') : null);
  };

  const existingIds = existing.map((p) => p.id);
  const allIds = [...existingIds, ...justUploaded.filter((id) => !existingIds.includes(id))];

  return (
    <div>
      {/* Staged only while the batch uploads; the thumbnails below are the
          persisted list, so the staging list is cleared once each upload
          settles. */}
      <FileUpload
        files={staged}
        onChange={(next: UploadFile[]) => {
          if (next.length === 0) return;
          setStaged(next);
          void handleFiles(next.map((f) => f.file)).finally(() => setStaged([]));
        }}
        multiple
        accept={['image/*']}
        capture="environment"
        label={uploading ? 'Uploading...' : 'Take or add photos'}
        disabled={uploading}
      />

      {error && (
        <p className="mt-2 text-[13px] text-red-700 dark:text-red-300">{error}</p>
      )}

      {allIds.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {allIds.map((id) => (
            <StorePhotoThumb key={id} photoId={id} />
          ))}
        </div>
      )}
    </div>
  );
}

// Renders a single photo thumbnail via a presigned GET. Falls back to a neutral
// placeholder while the URL loads or if it 404s.
export function StorePhotoThumb({ photoId }: { photoId: number }) {
  const { data, isLoading, error } = useStorePhotoUrl(photoId);
  if (isLoading || error || !data?.url) {
    return (
      <div className="flex aspect-square items-center justify-center rounded-lg bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted">
        <Camera className="h-5 w-5" aria-hidden />
      </div>
    );
  }
  return (
    <a href={data.url} target="_blank" rel="noopener noreferrer" className="block">
      <img
        src={data.url}
        alt="Store photo"
        className="aspect-square w-full rounded-lg object-cover"
      />
    </a>
  );
}
